import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {InsightsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isValidationRulesSupportedType, TagSchema} from 'constants/RegistryConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SettingsIconDropdownItem} from 'nav/sidepanel/SettingsIconDropdown';
import SetPrecisionModal from 'notebook/tables/toolbar/SetPrecisionModal';
import ConfigureEntityLineagePathModal from 'registry/child-entities/ConfigureEntityLineagePathModal';
import {
  ParentLineagePathsQuery,
  ParentLineagePathsQueryVariables,
} from 'registry/field/__generated/ParentLineagePathsQuery';
import {AdvancedSettingsDropdown} from 'schemas/fields/AdvancedSettingsDropdown';
import {AdvancedSettingsIconProps, getUnitDropdownOption} from 'schemas/fields/AdvancedSettingsIconUtils';
import FieldChangeWarningModal, {FieldChangeWarehouseWarning} from 'schemas/fields/FieldChangeWarningModal';
import {
  FieldType,
  getRequiredLinkTagSchema,
  SchemaField,
  SchemaFieldEdge,
} from 'schemas/fields/SchemaFieldConstants';
import {SetUneditableFieldModal} from 'schemas/fields/UneditableFields';
import {UpdateTagSchemaParams} from 'tag-schema/TagSchemaDAO';
import runModal from 'ui-library/runModal';
import omitKey from 'util/omitKey';
import {ElementMouseEvent, Optional} from 'util/Types';
import {BaseValidationRuleModalContainer} from 'validation_rules/components/simple_ui/BaseValidationRuleModal';
import {BooleanFieldModalContainer} from 'validation_rules/components/simple_ui/BooleanFieldModal';
import {
  IntegerFieldModalContainer,
  FloatFieldModalContainer,
} from 'validation_rules/components/simple_ui/NumericFieldModal';
import PartLinkFieldModal from 'validation_rules/components/simple_ui/PartLinkFieldModal';
import {TextFieldModalContainer} from 'validation_rules/components/simple_ui/TextFieldModal';
import TranscriptionLinkFieldModal from 'validation_rules/components/simple_ui/TranscriptionLinkFieldModal';
import TranslationLinkFieldModal from 'validation_rules/components/simple_ui/TranslationLinkFieldModal';
import {
  SimpleRulesState,
  ValidationRuleModalContainerProps,
} from 'validation_rules/components/simple_ui/useSimpleRules';

export const PARENT_LINEAGE_PATHS_QUERY = gql`
  query ParentLineagePathsQuery($tagSchemaId: StrictString!, $targetSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        id
        parentLineagePaths(targetSchemaId: $targetSchemaId) {
          ... on JSONScalarBox {
            value
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function generateSchemaFieldUpdate(
  update: Pick<
    SchemaField,
    'decimalPrecision' | 'isUneditable' | 'unitApiIdentifier' | 'unitSymbol' | 'simpleRules'
  > & {isRequired?: boolean; isMulti?: boolean},
  fieldToUpdate: SchemaField,
  schema: TagSchema
): UpdateTagSchemaParams {
  const updatedField: SchemaField = {...fieldToUpdate, ...omitKey(update, 'unitSymbol')}; // unitSymbol is only used for display purposes on frontend
  return {
    fields: schema.allFields.map((f) => (f.id === fieldToUpdate.id ? updatedField : f)),
  };
}

interface QueriedProps {
  parentLineagePaths?: Array<Array<SchemaFieldEdge>>;
}

export type EntityFieldAdvancedSettingsIconProps = AdvancedSettingsIconProps & QueriedProps;

function EntityFieldAdvancedSettingsIcon(props: EntityFieldAdvancedSettingsIconProps): JSX.Element {
  const getEnforceLineageDropdownOption = (): SettingsIconDropdownItem => {
    const enforceLineageOptionDisabled =
      props.forFieldCreation || // TODO BNCH-87604: Support option during field creation
      props.schema == null ||
      props.parentLineagePaths == null ||
      props.parentLineagePaths.length === 0;
    return {
      name: 'Enforce parent lineage',
      disabled: enforceLineageOptionDisabled,
      onSelect: (_: ElementMouseEvent) => {
        runModal(ConfigureEntityLineagePathModal, {
          field: props.field,
          availablePaths: props.parentLineagePaths!,
          sourceSchema: props.schema!,
          targetSchema: getRequiredLinkTagSchema(props.field)!,
        });
      },
    };
  };

  const getDecimalPrecisionDropdownOption = (): SettingsIconDropdownItem => {
    const decimalPrecisionOptionDisabled = props.field.fieldType !== FieldType.FLOAT;
    return {
      name: 'Set display precision...',
      disabled: decimalPrecisionOptionDisabled,
      onSelect: (_: ElementMouseEvent) => {
        runModal(SetPrecisionModal, {
          open: true,
          precisionLink: {
            maxPrecision: 15,
            value: props.field.decimalPrecision,
            requestChange: (decimalPrecision: Optional<number>) => {
              props.onUpdateField({decimalPrecision});
            },
          },
        });
      },
    };
  };

  const getIsUneditableDropdownOption = (): SettingsIconDropdownItem => {
    const isUneditableOptionDisabled =
      props.forFieldCreation || // TODO BNCH-87604: Support option during field creation
      !BenchlingConfig.SCHEMAS_ENABLE_UNEDITABLE_FIELDS_CONFIGURATION ||
      props.schema == null ||
      props.schema.folderItemType === ModelTypeConstants.ENTRY;
    return {
      name: 'Toggle editability...',
      disabled: isUneditableOptionDisabled,
      onSelect: async () => {
        const fieldUpdate = await runModal(SetUneditableFieldModal, {field: props.field});
        if (fieldUpdate == null) {
          return;
        }
        props.onUpdateField(fieldUpdate);
      },
    };
  };

  function maybeGetValidationRuleModal(): React.ComponentType<ValidationRuleModalContainerProps> | null {
    if (BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      if (props.field.fieldType === FieldType.TRANSLATION_LINK) {
        return TranslationLinkFieldModal;
      }

      if (props.field.fieldType === FieldType.TRANSCRIPTION_LINK) {
        return TranscriptionLinkFieldModal;
      }

      if (props.field.fieldType === FieldType.PART_LINK) {
        return PartLinkFieldModal;
      }
    }

    if (
      BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3 &&
      props.itemType &&
      isValidationRulesSupportedType(props.itemType)
    ) {
      if (props.field.fieldType === FieldType.TEXT) {
        return TextFieldModalContainer;
      }

      if (props.field.fieldType === FieldType.INTEGER) {
        return IntegerFieldModalContainer;
      }

      if (props.field.fieldType === FieldType.FLOAT) {
        return FloatFieldModalContainer;
      }

      if (props.field.fieldType === FieldType.BOOLEAN) {
        return BooleanFieldModalContainer;
      }
    }

    // Show the base modal regardless of field type if M3 is enabled because we stuff the required and multi
    // options into the modal.
    if (BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3) {
      return BaseValidationRuleModalContainer;
    }

    return null;
  }

  function getValidationRuleModal(): React.ComponentType<ValidationRuleModalContainerProps> {
    const validationRuleModal = maybeGetValidationRuleModal();
    if (validationRuleModal) {
      return validationRuleModal;
    }
    assert(false, `Validation rules modal not found for field type: ${props.field.fieldType}`);
  }

  const getValidationRuleDropdownOption = (): SettingsIconDropdownItem => {
    let dropdownName = 'Set validation rules...';
    if (
      props.field.fieldType === FieldType.TRANSLATION_LINK ||
      props.field.fieldType === FieldType.TRANSCRIPTION_LINK ||
      props.field.fieldType === FieldType.PART_LINK
    ) {
      dropdownName = BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3
        ? 'Set autofill and validation rules...'
        : 'Set autofill rules...';
    }

    return {
      name: dropdownName,
      disabled: false,
      onSelect: () => {
        runModal(getValidationRuleModal(), {
          schemaField: props.field,
          schemaFieldSelectors: props.schemaFieldSelectors ?? [],
          onRequestSubmit: (simpleRulesState: SimpleRulesState) => {
            if (
              !props.forFieldCreation &&
              BenchlingConfig.ENABLE_WAREHOUSE &&
              (BenchlingConfig.ENABLE_USER_CREATED_WAREHOUSE_CREDENTIALS ||
                isFeatureEnabled({feature: InsightsFeature.INSIGHTS_DISCOVER})) &&
              props.field.isMulti !== simpleRulesState.legacySimpleRules.isMulti
            ) {
              runModal(FieldChangeWarningModal, {
                onRequestSubmit: () => {
                  props.onUpdateField({
                    simpleRules: simpleRulesState.simpleRules,
                    isRequired: simpleRulesState.legacySimpleRules.isRequired,
                    isMulti: simpleRulesState.legacySimpleRules.isMulti,
                  });
                },
                content: (
                  <FieldChangeWarehouseWarning
                    fromFieldDefinition={props.field.fieldType}
                    isMultiMoreStrict={!simpleRulesState.legacySimpleRules.isMulti}
                  />
                ),
              });
            } else {
              props.onUpdateField({
                simpleRules: simpleRulesState.simpleRules,
                isRequired: simpleRulesState.legacySimpleRules.isRequired,
                isMulti: simpleRulesState.legacySimpleRules.isMulti,
              });
            }
          },
          onRequestClose: () => {},
          enableMultiSelectOption: !props.disableMultiSelect,
        });
      },
    };
  };

  const getPopulateFromAnotherFieldDropdownOption = (): SettingsIconDropdownItem => {
    const populateFromAnotherFieldOptionDisabled = !(
      BenchlingConfig.SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION &&
      BenchlingConfig.SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION_TO_EXISTING_FIELDS &&
      props.requestPopulateFromAnotherField
    );
    return {
      name: 'Populate from another field',
      disabled: populateFromAnotherFieldOptionDisabled,
      onSelect: () => {
        props.requestPopulateFromAnotherField!(props.field);
      },
    };
  };

  const shouldShowPopulateFromAnotherFieldDropdownOption = (): boolean => {
    const fieldMigrationEnabled =
      BenchlingConfig.SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION ||
      BenchlingConfig.SCHEMAS_ENABLE_MANUAL_SCHEMA_FIELD_MIGRATION;
    if (
      !props.schema ||
      !(fieldMigrationEnabled && BenchlingConfig.SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION_TO_EXISTING_FIELDS)
    ) {
      return false;
    }
    const unarchivedFields = props.schema.allFields.filter((field) => field.archiveRecord == null);
    return unarchivedFields.length >= 2;
  };

  const getManualFieldMigrationDropdownOption = (): SettingsIconDropdownItem => ({
    name: 'Migrate to new field',
    onSelect: () => {
      props.handleFieldMigration?.();
    },
  });

  const shouldShowManualFieldMigrationDropdownOption = (): boolean => {
    if (!props.schema) {
      return false;
    }
    if (!BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME) {
      return false;
    }
    return BenchlingConfig.SCHEMAS_ENABLE_MANUAL_SCHEMA_FIELD_MIGRATION;
  };

  const options: Array<SettingsIconDropdownItem> = [
    getEnforceLineageDropdownOption(),
    getDecimalPrecisionDropdownOption(),
    getIsUneditableDropdownOption(),
    getUnitDropdownOption(props),
  ];
  if (shouldShowPopulateFromAnotherFieldDropdownOption()) {
    options.push(getPopulateFromAnotherFieldDropdownOption());
  }
  if (shouldShowManualFieldMigrationDropdownOption()) {
    options.push(getManualFieldMigrationDropdownOption());
  }
  if (maybeGetValidationRuleModal() != null) {
    options.push(getValidationRuleDropdownOption());
  }

  return <AdvancedSettingsDropdown options={options} />;
}

function QueryingEntityFieldAdvancedSettingsIcon(props: AdvancedSettingsIconProps): JSX.Element {
  const maybeRequiredLinkTagSchema = getRequiredLinkTagSchema(props.field);
  if (maybeRequiredLinkTagSchema && props.schema?.id != null) {
    return (
      <BenchlingQuery<ParentLineagePathsQuery, ParentLineagePathsQueryVariables>
        query={PARENT_LINEAGE_PATHS_QUERY}
        variables={{tagSchemaId: props.schema.id, targetSchemaId: maybeRequiredLinkTagSchema.id}}
        renderLoading={() => <EntityFieldAdvancedSettingsIcon {...props} parentLineagePaths={[]} />}
      >
        {(data) => {
          const tagSchema = data.tagSchema;
          assert(!isInaccessible(tagSchema), 'Expected tag schema currently being viewed to be readable');
          const pathsOrInaccessible = tagSchema.parentLineagePaths;

          if (isInaccessible(pathsOrInaccessible)) {
            return <EntityFieldAdvancedSettingsIcon {...props} parentLineagePaths={[]} />;
          }
          return (
            <EntityFieldAdvancedSettingsIcon {...props} parentLineagePaths={pathsOrInaccessible.value} />
          );
        }}
      </BenchlingQuery>
    );
  }
  return <EntityFieldAdvancedSettingsIcon {...props} parentLineagePaths={[]} />;
}

export default QueryingEntityFieldAdvancedSettingsIcon;
